// ParentLinkStudent.js — updated
// src/ParentLinkStudent.js
// ============================================================
// ScholarSync AI — Parent: Link / Create / Invite Student
// ============================================================
// Three-option hub:
//   1. Link Existing Account (lookup by email)
//   2. Create Their Profile (Path A — parent fills profile)
//   3. Invite My Child (Path B — send registration link, future)
// ============================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

export default function ParentLinkStudent() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // mode: null = hub, 'link' = existing lookup, 'create' = Path A, 'invite' = Path B
  const [mode, setMode] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [childCount, setChildCount] = useState(0);

  // Check plan type + current child count
  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/stripe/subscription-status`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/parent-links/my-students`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      }).then(r => r.ok ? r.json() : null),
    ]).then(([sub, links]) => {
      if (sub?.planType) setPlanType(sub.planType);
      if (links?.students) setChildCount(links.students.length);
    }).catch(() => {});
  }, [user]);

  const atLimit = (planType === 'individual' && childCount >= 1) || (planType === 'family' && childCount >= 3);

  // Shared state
  const [email, setEmail]         = useState('');
  const [childName, setChildName] = useState('');
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [loading, setLoading]     = useState(false);

  // Link-existing state
  const [student, setStudent]     = useState(null);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${user.token}`,
  };

  const resetState = () => {
    setEmail('');
    setChildName('');
    setError('');
    setSuccess('');
    setLoading(false);
    setStudent(null);
  };

  const goBack = () => {
    resetState();
    setMode(null);
  };

  // ── Link Existing ──────────────────────────────────────────
  const handleLookup = async (e) => {
    e.preventDefault();
    setError(''); setStudent(null); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/parent-links/lookup-student?email=${encodeURIComponent(email)}`,
        { headers }
      );
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Student not found.'); setLoading(false); return; }
      setStudent(data.student);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/parent-links`, {
        method: 'POST', headers,
        body: JSON.stringify({ student_id: student.student_id }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to link student.'); setLoading(false); return; }
      setSuccess(`Successfully linked to ${student.full_name}!`);
      setTimeout(() => navigate('/parent/dashboard'), 1500);
    } catch (err) {
      setError('Network error — please try again.');
      setLoading(false);
    }
  };

  // ── Path B: Invite Child ───────────────────────────────────
  const handleInvite = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/parent-links/create-student`, {
        method: 'POST', headers,
        body: JSON.stringify({ childName, childEmail: email, path: 'invite' }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to send invite.'); setLoading(false); return; }
      setSuccess(data.emailSent
        ? `Invite sent to ${email}! ${childName} will receive an email with a link to create their profile.`
        : `Student created, but the email couldn't be sent. Share this link manually: ${window.location.origin}/register?invite=${data.studentId}`
      );
      setTimeout(() => navigate('/parent/dashboard'), 4000);
    } catch (err) {
      setError('Network error — please try again.');
      setLoading(false);
    }
  };
  // ── Path A: Create Student ─────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/parent-links/create-student`, {
        method: 'POST', headers,
        body: JSON.stringify({ childName, childEmail: email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to create student.'); setLoading(false); return; }
      // Redirect to profile form with the new studentId
      navigate(`/profile/new?studentId=${data.studentId}`);
    } catch (err) {
      setError('Network error — please try again.');
      setLoading(false);
    }
  };

  // ── Hub View ───────────────────────────────────────────────
    if (mode === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <button onClick={() => navigate('/parent/dashboard')} style={styles.backBtn}>
            ← Back to Dashboard
          </button>

          <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
          <h1 style={styles.title}>Add a Student</h1>

          {atLimit ? (
            planType === 'individual' ? (
              <div>
                <p style={styles.subtitle}>
                  Your Individual plan supports 1 student. Upgrade to Family ($97/yr) to add up to 3 students.
                </p>
                <button
                  onClick={() => navigate('/parent/upgrade')}
                  style={{ ...styles.primaryBtn, background: '#2563EB', marginBottom: 12 }}
                >
                  Upgrade to Family Plan →
                </button>
                <button onClick={() => navigate('/parent/dashboard')} style={styles.backBtn}>
                  ← Back to Dashboard
                </button>
              </div>
            ) : (
              <div>
                                <p style={styles.subtitle}>
                  Your Family plan supports up to 3 students, and all 3 spots are filled. Need to add another child? We can help.
                </p>
                <a
                  href="mailto:theteam@yourscholarshipfinder.com?subject=Family%20plan%20—%20additional%20students"
                  style={{ ...styles.primaryBtn, textDecoration: 'none', display: 'block', textAlign: 'center' }}
                >
                  Contact Us
                </a>
              </div>
            )
          ) : (
            <>
              <p style={styles.subtitle}>
                Choose how you'd like to connect your child to your account.
              </p>
              <div style={styles.optionList}>
                <button style={styles.optionCard} onClick={() => setMode('create')}>
                  <div style={styles.optionIcon}>📝</div>
                  <div>
                    <div style={styles.optionTitle}>Create Their Profile</div>
                    <div style={styles.optionDesc}>
                      Fill out your child's scholarship profile on their behalf
                    </div>
                  </div>
                </button>

                <button style={styles.optionCard} onClick={() => setMode('invite')}>
                  <div style={styles.optionIcon}>✉️</div>
                  <div>
                    <div style={styles.optionTitle}>Invite My Child</div>
                    <div style={styles.optionDesc}>
                      Send your child a link to create their own profile
                    </div>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Link Existing View ─────────────────────────────────────
  if (mode === 'link') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <button onClick={goBack} style={styles.backBtn}>← Back to Options</button>

                    <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
          <h1 style={styles.title}>Link Existing Account</h1>
          <p style={styles.subtitle}>
            Enter the email address your child used to create their Your Scholarship Finder account.
          </p>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <form onSubmit={handleLookup} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Student's Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required style={styles.input} placeholder="child@example.com" />
            </div>
            <button type="submit" disabled={loading}
              style={{...styles.primaryBtn, opacity: loading ? 0.6 : 1}}>
              {loading ? 'Searching…' : 'Find Student'}
            </button>
          </form>

          {student && !success && (
            <div style={styles.confirmCard}>
              <p style={styles.confirmLabel}>Student found:</p>
              <div style={styles.confirmDetails}>
                <p style={styles.confirmName}>{student.full_name}</p>
                <p style={styles.confirmMeta}>
                  {student.state_of_residence || 'State not set'}
                  {student.graduation_date
                    ? ` · Class of ${new Date(student.graduation_date).getFullYear()}`
                    : ''}
                </p>
              </div>
              <button onClick={handleLink} disabled={loading} style={styles.darkBtn}>
                {loading ? 'Linking…' : 'Link This Student to My Account'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Path B: Invite View ────────────────────────────────────
  if (mode === 'invite') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <button onClick={goBack} style={styles.backBtn}>← Back to Options</button>

                    <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
          <h1 style={styles.title}>Invite Your Child</h1>
          <p style={styles.subtitle}>
            Enter your child's name and email. We'll send them a link to create
            their own profile, and you'll be copied on the email.
          </p>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          {!success && (
            <form onSubmit={handleInvite} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Child's Full Name</label>
                <input type="text" value={childName} onChange={(e) => setChildName(e.target.value)}
                  required style={styles.input} placeholder="First Last" />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Child's Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required style={styles.input} placeholder="child@example.com" />
              </div>
              <button type="submit" disabled={loading}
                style={{...styles.primaryBtn, opacity: loading ? 0.6 : 1}}>
                {loading ? 'Sending…' : 'Send Invite →'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }
  // ── Path A: Create Profile View ────────────────────────────
  if (mode === 'create') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <button onClick={goBack} style={styles.backBtn}>← Back to Options</button>

                    <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
          <h1 style={styles.title}>Create Your Child's Profile</h1>
          <p style={styles.subtitle}>
            Enter your child's basic info below. You'll fill out their full
            scholarship profile on the next screen.
          </p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleCreate} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Child's Full Name</label>
              <input type="text" value={childName} onChange={(e) => setChildName(e.target.value)}
                required style={styles.input} placeholder="First Last" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Child's Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required style={styles.input} placeholder="child@example.com" />
            </div>
            <button type="submit" disabled={loading}
              style={{...styles.primaryBtn, opacity: loading ? 0.6 : 1}}>
              {loading ? 'Creating…' : 'Continue to Profile →'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null;
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#6B7280',
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
    marginBottom: '20px',
  },
   logo: {
    maxWidth: '220px',
    height: 'auto',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '13px',
    marginBottom: '16px',
  },
  success: {
    background: '#F0FDF4',
    border: '1px solid #BBF7D0',
    borderRadius: '8px',
    padding: '12px',
    color: '#16A34A',
    fontSize: '13px',
    marginBottom: '16px',
  },
  form: {
    marginBottom: '20px',
  },
  field: {
    marginBottom: '14px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  primaryBtn: {
    width: '100%',
    padding: '10px',
    background: '#374151',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  darkBtn: {
    width: '100%',
    padding: '12px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmCard: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '20px',
  },
  confirmLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
  },
  confirmDetails: {
    marginBottom: '16px',
  },
  confirmName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  confirmMeta: {
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '4px',
  },
  // ── Hub option cards ──
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  optionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    padding: '16px 20px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s',
  },
  optionIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  optionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '2px',
  },
  optionDesc: {
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: '1.4',
  },
  comingSoon: {
    display: 'inline-block',
    marginLeft: '8px',
    padding: '2px 8px',
    background: '#E5E7EB',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#6B7280',
    verticalAlign: 'middle',
  },
};